/**
 * `/forgot-password` — code-based password reset (2026-05-29).
 *
 * Replaces the prior magic-link recovery flow. Corporate email security
 * gateways (Mimecast / Proofpoint / Microsoft Safe Links) pre-fetch every
 * URL in an inbound email, which consumed Supabase's single-use recovery
 * link before the user could click it — so the reset silently failed (or,
 * worse, the verify-redirect established a full session and bounced the user
 * to /cockpit instead of a reset form). A 6-digit code can't be "clicked" by
 * a scanner, so it survives the gateway.
 *
 * Single-page, two-step flow (no link, no cross-page navigation):
 *   1. `request` — user enters email → `resetPasswordForEmail(email)` sends
 *      the recovery OTP (the email template emits `{{ .Token }}`).
 *   2. `verify`  — user enters the code + a new password →
 *      `verifyOtp({type:'recovery'})` establishes a recovery-tier session →
 *      `updateUser({password})` → `signOut()` → `/login?reset=success`.
 *
 * The recovery-tier session is signed out immediately after the password
 * update (product decision 2026-05-20, carried over from the old
 * reset-password page) so the user signs in fresh with the new password.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { AuthLogo } from '@/components/layout/AuthLogo';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { SupportLink } from '@/components/ui';
import { posthog } from '../lib/posthog';

type Step = 'request' | 'verify';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // No `redirectTo` — the recovery email carries a code, not a link.
    const { error: err } = await supabase.auth.resetPasswordForEmail(email);

    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    // Phase 3 § 3.3 — password reset requested (action-driven; no StrictMode guard).
    posthog.capture('password_reset_requested', {});
    setCode('');
    setStep('verify');
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    // `type: 'recovery'` — the verification type for resetPasswordForEmail
    // codes (distinct from `'email'` used by the sign-in OTP). On success
    // GoTrue establishes a recovery-tier session scoped to allow updateUser.
    const { error: verifyErr } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: 'recovery',
    });
    if (verifyErr) {
      setLoading(false);
      setError(verifyErr.message);
      return;
    }

    const { error: updateErr } = await supabase.auth.updateUser({ password });
    if (updateErr) {
      setLoading(false);
      setError(updateErr.message);
      return;
    }

    // Sign out the recovery-tier session + route to /login so the user signs
    // in fresh with the new password (cleaner than auto-upgrading to a full
    // session and bouncing to /cockpit).
    await supabase.auth.signOut();
    navigate({ to: '/login', search: { reset: 'success' as const } });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-void">
      <div className="w-full max-w-sm px-8 py-10">
        <AuthLogo />
        <p className="text-sm text-text-muted font-mono text-center mb-6">
          Reset your password
        </p>

        {step === 'request' ? (
          <>
            <form onSubmit={handleRequest} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                  EMAIL
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="w-full px-4 py-3.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-md outline-none focus:border-accent/30 transition-colors"
                />
              </div>

              {error && (
                <div className="px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px] flex flex-col gap-2">
                  <p className="text-danger font-mono text-sm">{error}</p>
                  <SupportLink
                    variant="inline"
                    subject="Password reset error"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 mt-1 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate({ to: '/login' })}
                className="text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted transition-colors"
              >
                &larr; Back to Sign In
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-text-muted font-mono leading-relaxed text-center mb-4">
              Enter the 6-digit code we sent to{' '}
              <span className="text-text-bright">{email}</span> and choose a new
              password.
            </p>
            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                  CODE
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="Reset code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  required
                  autoFocus
                  className="w-full px-4 py-3.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-xl text-center tracking-[0.5em] outline-none focus:border-accent/30 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                  NEW PASSWORD
                </label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  aria-label="New password"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                  CONFIRM PASSWORD
                </label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  aria-label="Confirm new password"
                />
              </div>

              {error && (
                <div className="px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px] flex flex-col gap-2">
                  <p className="text-danger font-mono text-sm">{error}</p>
                  <SupportLink
                    variant="inline"
                    subject="Password reset error"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 6}
                data-testid="reset-password-submit"
                className="w-full py-3.5 mt-1 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
              >
                {loading ? 'Setting password...' : 'Set Password'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setStep('request');
                  setError(null);
                }}
                className="text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted transition-colors"
              >
                &larr; Use a different email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
